/**
 * Hears the page come back from the browser's back-forward cache.
 *
 * A page that leaves for Stripe (checkout, the billing portal) marks its
 * button busy and never clears it, since the page is going away. Pressing
 * Back can bring that very page back, frozen as it was, busy buttons and
 * all, with nothing left running to clear them. The browser says so with a
 * `pageshow` whose `persisted` is true; this is where a page puts itself
 * right again.
 *
 * Who uses it: features/billing SubscribePage and PauseOffer, and
 * features/gift GiftPage, each of which sends the browser to Stripe.
 *
 * @module usePageRestored
 */

import { useEffect, useRef } from 'react';

/**
 * Calls back each time the page is restored from the back-forward cache.
 *
 * @param onRestored - What to put right; the latest one is called.
 */
export function usePageRestored(onRestored: () => void): void {
  const latest = useRef(onRestored);
  latest.current = onRestored;

  useEffect(() => {
    /**
     * The browser showed the page; only a restore matters here.
     *
     * @param event - The pageshow event.
     */
    const shown = (event: PageTransitionEvent): void => {
      if (event.persisted) latest.current();
    };
    window.addEventListener('pageshow', shown);
    return () => window.removeEventListener('pageshow', shown);
  }, []);
}
